import { ChevronLeft, CrossLarge } from "@acto/web-common/components";
import { css, styled } from "@pigment-css/react";
import { clsx } from "clsx";
import { ReactNode, useEffect, useState } from "react";

interface ManagementSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  level?: 1 | 2 | 3;
  children: ReactNode;
  isLevel1Shrunk?: boolean;
  isLevel2Shrunk?: boolean;
  headerControls?: ReactNode;
  /** Replaces the close control with a back control that calls this instead of `onClose`. */
  back?: { onBack: () => void; label: string };
  leadingControls?: ReactNode;
  widePanel?: boolean;
}

const defaultPanelWidth = "740px";
const shrunkPanelVisibleWidth = "220px";
const shrunkPanelRightOffset = `calc(12px + ${defaultPanelWidth} - ${shrunkPanelVisibleWidth})`;

const panelBase = css(({ theme }) => ({
  position: "fixed",
  top: "12px",
  bottom: "12px",
  backgroundColor: theme.managementView.colors.neutral[0],
  transition: "transform 0.3s ease, width 0.3s ease, top 0.3s ease, bottom 0.3s ease, right 0.3s ease",
  display: "flex",
  flexDirection: "column",
  boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
  borderTopLeftRadius: theme.managementView.radius[24],
  borderBottomLeftRadius: theme.managementView.radius[24],
  borderTopRightRadius: theme.managementView.radius[24],
  borderBottomRightRadius: theme.managementView.radius[24],
  overflow: "hidden",
}));

const panelOpen = css({
  transform: "translateX(0)",
  pointerEvents: "auto",
});

const panelClosed = css({
  transform: "translateX(calc(100% + 12px))",
  pointerEvents: "none",
});

const panelLevel1 = css({
  zIndex: 10001,
  width: defaultPanelWidth,
  right: "12px",
});

const panelLevel1Shrunk = css({
  top: "24px",
  bottom: "24px",
  right: shrunkPanelRightOffset,
});

const panelLevel2 = css({
  zIndex: 10002,
  width: defaultPanelWidth,
  right: "12px",
});

const panelLevel2Shrunk = css({
  top: "24px",
  bottom: "24px",
  right: shrunkPanelRightOffset,
});

const panelLevel3 = css({
  zIndex: 10003,
  width: defaultPanelWidth,
  right: "12px",
});

const widePanelStyles = css({
  width: defaultPanelWidth,
});

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[8],
  paddingTop: theme.managementView.spacing[24],
  paddingRight: theme.managementView.spacing[24],
  paddingBottom: theme.managementView.spacing[24],
  paddingLeft: theme.managementView.spacing[24],
}));

const CloseButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: "50px",
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[24]}`,
  backgroundColor: theme.managementView.colors.neutral[4],
  color: theme.managementView.colors.neutral[90],
  cursor: "pointer",

  flexShrink: 0,

  "& svg": {
    width: "20px",
    height: "20px",
  },

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[8],
  },
}));

const HeaderControls = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
}));

const LeadingControls = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  minWidth: 0,
}));

const Content = styled("div")(({ theme }) => ({
  flex: 1,
  padding: theme.managementView.spacing[24],
  overflowY: "auto",
  overscrollBehavior: "contain",
  color: theme.managementView.colors.neutral[90],
}));

const useMountTransition = (isOpen: boolean, transitionDuration = 300) => {
  const [shouldMount, setShouldMount] = useState(isOpen);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldMount(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return;
    }

    setIsTransitioning(false);
    const timer = setTimeout(() => {
      setShouldMount(false);
    }, transitionDuration);
    return () => clearTimeout(timer);
  }, [isOpen, transitionDuration]);

  return { shouldMount, isTransitioning };
};

export const ManagementSidepanel = ({
  isOpen,
  onClose,
  level = 1,
  children,
  isLevel1Shrunk = false,
  isLevel2Shrunk = false,
  headerControls,
  back,
  leadingControls,
  widePanel = false,
}: ManagementSidepanelProps) => {
  const { shouldMount, isTransitioning } = useMountTransition(isOpen, 300);

  const panelClasses = clsx(
    panelBase,
    isTransitioning ? panelOpen : panelClosed,
    level === 1 ? panelLevel1 : level === 2 ? panelLevel2 : panelLevel3,
    level === 1 && isLevel1Shrunk && panelLevel1Shrunk,
    level === 2 && isLevel2Shrunk && panelLevel2Shrunk,
  );

  if (!shouldMount) {
    return null;
  }

  const closeControl = back ? (
    <CloseButton onClick={back.onBack} aria-label={back.label}>
      <ChevronLeft />
    </CloseButton>
  ) : (
    <CloseButton onClick={onClose}>
      <CrossLarge />
    </CloseButton>
  );

  return (
    <div className={clsx(panelClasses, widePanel && widePanelStyles)}>
      <Header>
        {leadingControls ? (
          <LeadingControls>
            {closeControl}
            {leadingControls}
          </LeadingControls>
        ) : (
          closeControl
        )}
        {headerControls && <HeaderControls>{headerControls}</HeaderControls>}
      </Header>
      <Content>{children}</Content>
    </div>
  );
};
